import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  IPlaidAccountMetadata,
  PLAID_KEY,
  PlaidEnvironmentsEnum,
} from '../../models/plaid.model';
import { WindowRefService } from 'i2g-ng-utils';
import * as Plaid from './plaid.service.interface';

/**
 * Docs: https://plaid.com/docs/api/tokens/#linktokencreate
 */
const SUPPORTED_PLAID_LANGUAGES = ['en', 'fr', 'es', 'nl'];

@Injectable({
  providedIn: 'root',
})
export class PlaidService {
  private readonly _windowName = 'Plaid';
  private readonly _scriptElementId = 'script-element-plaid';
  private _plaidHandler: any;
  private _ready: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );

  ready$: Observable<boolean> = this._ready.asObservable();

  constructor(private _windowRefService: WindowRefService) {}

  loadScript(): void {
    // global variable is present = script was already loaded
    if (this._windowPlaid()) {
      this._ready.next(true);
      return;
    }

    const url = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';

    // prepare script elem
    const scriptElem = document.createElement('script');
    scriptElem.id = this._scriptElementId;
    scriptElem.innerHTML = '';
    scriptElem.onload = () => this._ready.next(true);
    scriptElem.src = url;
    scriptElem.async = true;
    scriptElem.defer = true;

    // add script to header
    document.getElementsByTagName('head')[0].appendChild(scriptElem);
  }

  initialize(
    onSuccess: (publicToken: string, metadata?: IPlaidAccountMetadata) => void,
    plaidEnvironment: PlaidEnvironmentsEnum,
    locale: string = 'en-US',
    onExit?: (err: any, metadata?: any) => void,
    onLoad?: () => void,
    onEvent?: (eventName: string, metadata?: any) => void
  ) {
    const language = locale.split('-')[0];
    try {
      this._plaidHandler = this._windowPlaid().create({
        clientName: 'Invoice2go', // Name to be displayed on plaid popup
        env: plaidEnvironment,
        key: PLAID_KEY,
        language: !SUPPORTED_PLAID_LANGUAGES.includes(language)
          ? 'en'
          : language,
        product: ['auth'],
        selectAccount: true,
        onSuccess: (publicToken: string, metadata: any) => {
          const plaidAccountMetadata = metadata as IPlaidAccountMetadata;
          onSuccess(publicToken, plaidAccountMetadata);
        },
        onExit,
        onLoad,
        onEvent,
      });
    } catch (error) {
      throw new Error(error);
    }
  }

  /**
   * We need a new way to initialise Plaid since
   * our implementation above is using the deprecated
   * flow
   */
  initializeV2(
    linkToken: string,
    plaidEnvironment: PlaidEnvironmentsEnum,
    locale: string = 'en-US',
    onSuccess: Plaid.OnSuccess,
    onLoad?: () => void,
    onEvent?: (eventName: string, metadata?: any) => void,
    onExit?: (err: any, metadata?: any) => void
  ) {
    const language = locale.split('-')[0];
    try {
      this._plaidHandler = (this._windowPlaid() as Plaid.Global).create({
        clientName: 'Invoice2go', // Name to be displayed on plaid popup
        env: plaidEnvironment,
        token: linkToken,
        language: !SUPPORTED_PLAID_LANGUAGES.includes(language)
          ? 'en'
          : (language as Plaid.Language),
        product: ['auth'],
        onSuccess,
        onExit,
        onLoad,
        onEvent,
      });
    } catch (error) {
      throw new Error(error);
    }
  }

  open() {
    try {
      this._plaidHandler.open();
    } catch (error) {
      throw new Error(error);
    }
  }

  destroyScript(): void {
    const scriptElem = document.getElementById(this._scriptElementId);

    if (scriptElem) {
      scriptElem.remove();
    }
  }

  private _windowPlaid() {
    return this._windowRefService.nativeWindow[this._windowName];
  }
}

// Exports the types
export { Plaid };
